import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import { useAuth } from "../App";
import { Plus, CheckCircle2, Circle, AlertTriangle, ChevronDown, ChevronRight, FileText, Shield, Layers, Users, ClipboardCheck, BarChart3, Play, Eye, X, Loader2, UserPlus, ExternalLink, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  quality_management: { label: "Quality Management", icon: Shield, color: "#36A9E1" },
  risk_management: { label: "Risk Management", icon: AlertTriangle, color: "#FBBF24" },
  regulatory: { label: "Regulatory", icon: FileText, color: "#F87171" },
  supplier_quality: { label: "Supplier Quality", icon: Users, color: "#4ADE80" },
  corrective_actions: { label: "CAPA", icon: ClipboardCheck, color: "#A78BFA" },
  audits: { label: "Audits", icon: BarChart3, color: "#38BDF8" },
  training: { label: "Training", icon: Layers, color: "#FB923C" },
  software_lifecycle: { label: "Software Lifecycle", icon: FileText, color: "#36A9E1" },
  cybersecurity: { label: "Cybersecurity", icon: Shield, color: "#F87171" },
  data_integrity: { label: "Data Integrity", icon: Shield, color: "#FBBF24" },
  usability: { label: "Usability", icon: Users, color: "#4ADE80" },
  general: { label: "General", icon: ClipboardCheck, color: "#8B95A5" }
};

export default function OnboardingChecklistPage() {
  const { t } = useTranslation(["operations", "nav"]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [view, setView] = useState("runs"); // "runs" | "templates" | "run-detail" | "readiness"
  const [selectedRun, setSelectedRun] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [showNewRun, setShowNewRun] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, runRes] = await Promise.all([
        fetch(`${API}/api/governance/onboarding/templates`, { credentials: "include" }),
        fetch(`${API}/api/governance/onboarding/runs`, { credentials: "include" })
      ]);
      const tplData = await tplRes.json();
      const runData = await runRes.json();
      setTemplates(tplData.templates || []);
      setRuns(runData.runs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchRun = async (runId) => {
    const res = await fetch(`${API}/api/governance/onboarding/runs/${runId}`, { credentials: "include" });
    const data = await res.json();
    setSelectedRun(data);
    setView("run-detail");
    // Pre-expand all categories
    const cats = {};
    (data.items || []).forEach(i => { cats[i.category] = true; });
    setExpandedCats(cats);
  };

  const fetchReadiness = async (runId) => {
    const res = await fetch(`${API}/api/governance/onboarding/runs/${runId}/readiness`, { credentials: "include" });
    const data = await res.json();
    setReadiness(data);
    setView("readiness");
  };

  const toggleItem = async (runId, itemId, currentCompleted) => {
    await fetch(`${API}/api/governance/onboarding/runs/${runId}/items/${itemId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ completed: !currentCompleted })
    });
    fetchRun(runId);
  };

  const updateEvidence = async (runId, itemId, notes) => {
    await fetch(`${API}/api/governance/onboarding/runs/${runId}/items/${itemId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ evidence_notes: notes })
    });
  };

  const createRun = async (templateId, clientName, clientEmail, notes) => {
    await fetch(`${API}/api/governance/onboarding/runs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ template_id: templateId, client_name: clientName, client_email: clientEmail, notes })
    });
    setShowNewRun(false);
    fetchData();
  };

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const isAdmin = user?.role === "admin";
  const isAuditor = user?.role === "auditor";

  // Group items by category
  const groupItems = (items) => {
    const groups = {};
    (items || []).forEach(item => {
      const cat = item.category || "general";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  };

  return (
    <QMSLayout title={t("nav:items.onboardingChecklist", { defaultValue: "Onboarding Checklist" })}>
      <div className="space-y-6" data-testid="onboarding-checklist-page">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-[#8B95A5]" data-testid="onboarding-subtitle">Standards-based client onboarding checklists and readiness tracking</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded overflow-hidden border border-[rgba(255,255,255,0.1)]">
              {[
                { id: "runs", label: "Checklist Runs" },
                { id: "templates", label: "Templates" }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setView(tab.id); setSelectedRun(null); setReadiness(null); }}
                  className={`px-4 py-2 text-sm transition-colors ${view === tab.id || (view === "run-detail" && tab.id === "runs") || (view === "readiness" && tab.id === "runs") ? "bg-[#36A9E1] text-white" : "bg-[#1F222D] text-[#8B95A5] hover:text-white"}`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {(isAdmin || isAuditor) && (
              <button onClick={() => setShowNewRun(true)} className="btn-primary flex items-center gap-2" data-testid="new-run-btn">
                <Plus size={16} /> New Run
              </button>
            )}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="onboarding-kpis">
          <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
            <p className="kpi-label">Total Runs</p>
            <p className="kpi-value">{runs.length}</p>
          </div>
          <div className="kpi-card" style={{ "--kpi-accent": "#FBBF24" }}>
            <p className="kpi-label">In Progress</p>
            <p className="kpi-value">{runs.filter(r => r.status === "in_progress").length}</p>
          </div>
          <div className="kpi-card" style={{ "--kpi-accent": "#4ADE80" }}>
            <p className="kpi-label">Ready</p>
            <p className="kpi-value">{runs.filter(r => r.status === "ready").length}</p>
          </div>
          <div className="kpi-card" style={{ "--kpi-accent": "#38BDF8" }}>
            <p className="kpi-label">Templates</p>
            <p className="kpi-value">{templates.length}</p>
          </div>
        </div>

        {/* Loading */}
        {loading && <div className="text-center py-12 text-[#8B95A5]">{t("common:status.loading")}</div>}

        {/* RUNS LIST */}
        {!loading && (view === "runs") && (
          <div className="space-y-3" data-testid="runs-list">
            {runs.length === 0 ? (
              <div className="text-center py-12 text-[#8B95A5]">No onboarding runs yet. Create one from a template.</div>
            ) : (
              runs.map(run => (
                <div key={run.run_id} className="qms-card hover:border-[rgba(255,255,255,0.15)] transition-colors cursor-pointer" onClick={() => fetchRun(run.run_id)} data-testid={`run-card-${run.run_id}`}>
                  <div className="qms-card-body flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-[#38BDF8] font-mono text-xs">{run.run_id}</span>
                        <span className={`badge ${run.status === "ready" ? "badge-green" : run.status === "completed" ? "badge-blue" : "badge-yellow"}`}>
                          {run.status.replace("_", " ")}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold">{run.client_name}</h3>
                      <p className="text-[#8B95A5] text-sm">{run.template_name}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-[#8B95A5]">Progress</p>
                        <p className="text-lg font-bold text-white">{run.progress_pct}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#8B95A5]">Items</p>
                        <p className="text-sm text-white">{run.completed_items}/{run.total_items}</p>
                      </div>
                      <div className="w-24 h-2 bg-[#0D0F14] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${run.progress_pct === 100 ? "bg-[#4ADE80]" : "bg-[#36A9E1]"}`} style={{ width: `${run.progress_pct}%` }} />
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); fetchReadiness(run.run_id); }} className="text-xs text-[#38BDF8] hover:text-white flex items-center gap-1" data-testid={`readiness-btn-${run.run_id}`}>
                        <Eye size={14} /> Readiness
                      </button>
                      {isAdmin && (
                        <button onClick={(e) => { e.stopPropagation(); setShowAssignModal(run); }} className="text-xs text-[#A78BFA] hover:text-white flex items-center gap-1" data-testid={`assign-btn-${run.run_id}`}>
                          <UserPlus size={14} /> Assign
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/onboarding-review/${run.run_id}`); }} className="text-xs text-[#4ADE80] hover:text-white flex items-center gap-1" data-testid={`review-btn-${run.run_id}`}>
                        <ExternalLink size={14} /> Review
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* TEMPLATES LIST */}
        {!loading && view === "templates" && (
          <div className="space-y-3" data-testid="templates-list">
            {templates.map(tpl => (
              <div key={tpl.template_id} className="qms-card" data-testid={`template-card-${tpl.template_id}`}>
                <div className="qms-card-body">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-[#38BDF8] font-mono text-xs mr-2">{tpl.template_id}</span>
                      <span className="badge badge-gray">{tpl.domain}</span>
                    </div>
                    <span className="text-sm text-[#8B95A5]">{tpl.items?.length || 0} items</span>
                  </div>
                  <h3 className="text-white font-semibold mb-1">{tpl.name}</h3>
                  <p className="text-[#8B95A5] text-sm mb-3">{tpl.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(groupItems(tpl.items)).map(([cat, items]) => {
                      const meta = CATEGORY_META[cat] || CATEGORY_META.general;
                      return (
                        <span key={cat} className="text-xs px-2 py-1 rounded" style={{ background: `${meta.color}15`, color: meta.color }}>
                          {meta.label} ({items.length})
                        </span>
                      );
                    })}
                  </div>
                  {(isAdmin || isAuditor) && (
                    <button onClick={() => { setShowNewRun(true); }} className="mt-3 text-xs text-[#36A9E1] hover:text-white flex items-center gap-1" data-testid={`use-template-${tpl.template_id}`}>
                      <Play size={12} /> Start Run
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* RUN DETAIL */}
        {!loading && view === "run-detail" && selectedRun && (
          <div className="space-y-4" data-testid="run-detail">
            <div className="flex items-center justify-between">
              <div>
                <button onClick={() => { setView("runs"); setSelectedRun(null); }} className="text-sm text-[#8B95A5] hover:text-white mb-2 flex items-center gap-1">
                  ← Back to Runs
                </button>
                <h2 className="text-white font-['Syne'] text-xl font-bold">{selectedRun.client_name}</h2>
                <p className="text-[#8B95A5] text-sm">{selectedRun.template_name} &middot; {selectedRun.run_id}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">{selectedRun.progress_pct}%</p>
                  <p className="text-xs text-[#8B95A5]">{selectedRun.completed_items}/{selectedRun.total_items} completed</p>
                </div>
                <button onClick={() => fetchReadiness(selectedRun.run_id)} className="btn-primary text-sm flex items-center gap-2" data-testid="view-readiness-btn">
                  <BarChart3 size={14} /> Gap Summary
                </button>
                <button onClick={() => navigate(`/onboarding-review/${selectedRun.run_id}`)} className="text-sm flex items-center gap-2 px-3 py-2 rounded bg-[#4ADE80]/10 text-[#4ADE80] hover:bg-[#4ADE80]/20" data-testid="review-submissions-btn">
                  <ExternalLink size={14} /> Review Submissions
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full h-3 bg-[#0D0F14] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${selectedRun.progress_pct === 100 ? "bg-[#4ADE80]" : "bg-[#36A9E1]"}`} style={{ width: `${selectedRun.progress_pct}%` }} />
            </div>

            {/* Items grouped by category */}
            {Object.entries(groupItems(selectedRun.items)).map(([cat, items]) => {
              const meta = CATEGORY_META[cat] || CATEGORY_META.general;
              const Icon = meta.icon;
              const completed = items.filter(i => i.completed).length;
              const expanded = expandedCats[cat] !== false;

              return (
                <div key={cat} className="qms-card" data-testid={`category-${cat}`}>
                  <div className="qms-card-body">
                    <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between py-1" data-testid={`toggle-cat-${cat}`}>
                      <div className="flex items-center gap-3">
                        <Icon size={18} style={{ color: meta.color }} />
                        <span className="text-white font-semibold">{meta.label}</span>
                        <span className="text-xs text-[#8B95A5]">({completed}/{items.length})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-1.5 bg-[#0D0F14] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${items.length ? (completed / items.length * 100) : 0}%`, background: meta.color }} />
                        </div>
                        {expanded ? <ChevronDown size={16} className="text-[#8B95A5]" /> : <ChevronRight size={16} className="text-[#8B95A5]" />}
                      </div>
                    </button>

                    {expanded && (
                      <div className="mt-3 space-y-2">
                        {items.sort((a, b) => a.order - b.order).map(item => (
                          <div key={item.item_id} className={`flex items-start gap-3 p-3 rounded border transition-colors ${item.completed ? "border-[rgba(74,222,128,0.2)] bg-[rgba(74,222,128,0.03)]" : "border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)]"}`} data-testid={`item-${item.item_id}`}>
                            <button onClick={() => toggleItem(selectedRun.run_id, item.item_id, item.completed)} className="mt-0.5 shrink-0" data-testid={`toggle-${item.item_id}`}>
                              {item.completed ? <CheckCircle2 size={20} className="text-[#4ADE80]" /> : <Circle size={20} className="text-[#8B95A5] hover:text-white" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`font-medium text-sm ${item.completed ? "text-[#4ADE80] line-through opacity-70" : "text-white"}`}>{item.title}</span>
                                {item.required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F87171]/10 text-[#F87171]">Required</span>}
                              </div>
                              {item.description && <p className="text-xs text-[#8B95A5] mb-2">{item.description}</p>}
                              {item.standard_refs?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {item.standard_refs.map(ref => (
                                    <span key={ref} className="text-[10px] px-1.5 py-0.5 rounded bg-[#36A9E1]/10 text-[#36A9E1] font-mono">{ref}</span>
                                  ))}
                                </div>
                              )}
                              {item.expected_artifacts?.length > 0 && (
                                <div className="text-xs text-[#8B95A5]">
                                  <span className="font-medium">Expected: </span>
                                  {item.expected_artifacts.join(", ")}
                                </div>
                              )}
                              {/* Evidence notes inline */}
                              <input
                                type="text"
                                defaultValue={item.evidence_notes || ""}
                                onBlur={(e) => updateEvidence(selectedRun.run_id, item.item_id, e.target.value)}
                                placeholder={t('operations:onboarding.evidencePlaceholder')}
                                className="mt-2 w-full bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] rounded px-2 py-1.5 text-xs text-white placeholder-[#555] outline-none focus:border-[#36A9E1]/40"
                                data-testid={`evidence-${item.item_id}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* READINESS VIEW */}
        {!loading && view === "readiness" && readiness && (
          <div className="space-y-4" data-testid="readiness-view">
            <button onClick={() => { setView("runs"); setReadiness(null); }} className="text-sm text-[#8B95A5] hover:text-white mb-2 flex items-center gap-1">
              ← Back to Runs
            </button>

            {/* Readiness header */}
            <div className="qms-card">
              <div className="qms-card-body flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h2 className="text-white font-['Syne'] text-xl font-bold mb-1">{readiness.client_name}</h2>
                  <p className="text-[#8B95A5] text-sm">{readiness.template_name}</p>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${readiness.overall_ready ? "text-[#4ADE80]" : "text-[#FBBF24]"}`}>{readiness.overall_progress}%</div>
                    <p className="text-xs text-[#8B95A5] mt-1">{readiness.completed_items}/{readiness.total_items} items</p>
                  </div>
                  <div className={`px-4 py-2 rounded-lg text-sm font-semibold ${readiness.overall_ready ? "bg-[#4ADE80]/10 text-[#4ADE80]" : "bg-[#FBBF24]/10 text-[#FBBF24]"}`} data-testid="readiness-badge">
                    {readiness.overall_ready ? "READY" : "NOT READY"}
                  </div>
                </div>
              </div>
            </div>

            {/* Category readiness */}
            <div className="qms-card">
              <div className="qms-card-header">Category Readiness</div>
              <div className="qms-card-body space-y-4">
                {readiness.category_readiness.map(cat => {
                  const meta = CATEGORY_META[cat.category] || CATEGORY_META.general;
                  return (
                    <div key={cat.category} data-testid={`readiness-cat-${cat.category}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white text-sm">{meta.label}</span>
                        <span className="text-[#8B95A5] text-xs">{cat.completed}/{cat.total} ({cat.progress_pct}%)</span>
                      </div>
                      <div className="w-full h-2 bg-[#0D0F14] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${cat.progress_pct}%`, background: cat.progress_pct === 100 ? "#4ADE80" : meta.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Standards coverage */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="qms-card">
                <div className="qms-card-header text-[#4ADE80]">Standards Covered ({readiness.standards_covered.length})</div>
                <div className="qms-card-body">
                  {readiness.standards_covered.length === 0 ? (
                    <p className="text-[#8B95A5] text-sm">No standards covered yet</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {readiness.standards_covered.map(std => (
                        <span key={std} className="text-xs px-2 py-1 rounded bg-[#4ADE80]/10 text-[#4ADE80] font-mono">{std}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="qms-card">
                <div className="qms-card-header text-[#F87171]">Standards with Gaps ({readiness.standards_with_gaps.length})</div>
                <div className="qms-card-body">
                  {readiness.standards_with_gaps.length === 0 ? (
                    <p className="text-[#4ADE80] text-sm">All referenced standards covered!</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {readiness.standards_with_gaps.map(std => (
                        <span key={std} className="text-xs px-2 py-1 rounded bg-[#F87171]/10 text-[#F87171] font-mono">{std}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Open gaps */}
            {readiness.gaps.length > 0 && (
              <div className="qms-card" data-testid="open-gaps">
                <div className="qms-card-header">Open Gaps ({readiness.gaps.length})</div>
                <div className="qms-card-body">
                  <table className="qms-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Category</th>
                        <th>Standard References</th>
                        <th>Expected Artifacts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readiness.gaps.map(gap => {
                        const meta = CATEGORY_META[gap.category] || CATEGORY_META.general;
                        return (
                          <tr key={gap.item_id}>
                            <td className="text-white">{gap.title}</td>
                            <td><span className="text-xs" style={{ color: meta.color }}>{meta.label}</span></td>
                            <td>
                              <div className="flex flex-wrap gap-1">
                                {gap.standard_refs.map(ref => (
                                  <span key={ref} className="text-[10px] px-1 py-0.5 rounded bg-[#36A9E1]/10 text-[#36A9E1] font-mono">{ref}</span>
                                ))}
                              </div>
                            </td>
                            <td className="text-[#8B95A5] text-xs">{gap.expected_artifacts.join(", ")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* NEW RUN MODAL */}
        {showNewRun && <NewRunModal templates={templates} onSubmit={createRun} onClose={() => setShowNewRun(false)} />}

        {/* ASSIGN TO CLIENT MODAL */}
        {showAssignModal && <AssignToClientModal run={showAssignModal} onClose={() => setShowAssignModal(null)} onAssigned={() => { setShowAssignModal(null); fetchData(); }} />}
      </div>
    </QMSLayout>
  );
}

function NewRunModal({ templates, onSubmit, onClose }) {
  const [templateId, setTemplateId] = useState(templates[0]?.template_id || "");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!clientName || !templateId) return;
    setSubmitting(true);
    await onSubmit(templateId, clientName, clientEmail, notes);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded w-full max-w-md" data-testid="new-run-modal">
        <div className="flex items-center justify-between p-5 border-b border-[rgba(255,255,255,0.07)]">
          <h3 className="font-['Syne'] text-lg font-semibold text-white">New Onboarding Run</h3>
          <button onClick={onClose} className="text-[#8B95A5] hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[#8B95A5] text-sm mb-1">Template</label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)} className="qms-select w-full" data-testid="template-select">
              {templates.map(tpl => (
                <option key={tpl.template_id} value={tpl.template_id}>{tpl.name} ({tpl.items?.length} items)</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[#8B95A5] text-sm mb-1">Client Name *</label>
            <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Company / Client name" className="qms-input w-full" data-testid="client-name-input" />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-sm mb-1">Client Email</label>
            <input value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="contact@company.com" type="email" className="qms-input w-full" data-testid="client-email-input" />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-sm mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional context..." rows={2} className="qms-input w-full resize-none" data-testid="notes-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSubmit} disabled={!clientName || submitting} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="create-run-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Start Run
            </button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function AssignToClientModal({ run, onClose, onAssigned }) {
  const [clientUserId, setClientUserId] = useState("user_dev_client");
  const [clientOrgId, setClientOrgId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleAssign = async () => {
    if (!clientUserId.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/client-onboarding/assignments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_user_id: clientUserId.trim(),
          run_id: run.run_id,
          client_org_id: clientOrgId.trim() || undefined
        })
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || t('operations:onboarding.assignFailed'));
        setSubmitting(false);
        return;
      }
      onAssigned();
    } catch (e) {
      setError("Network error");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded w-full max-w-md" data-testid="assign-client-modal">
        <div className="flex items-center justify-between p-5 border-b border-[rgba(255,255,255,0.07)]">
          <h3 className="font-['Syne'] text-lg font-semibold text-white">Assign to Client</h3>
          <button onClick={onClose} className="text-[#8B95A5] hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.05)]">
            <p className="text-xs text-[#8B95A5]">Assigning Run</p>
            <p className="text-sm text-white font-medium">{run.client_name}</p>
            <p className="text-xs text-[#38BDF8] font-mono">{run.run_id}</p>
          </div>
          <div>
            <label className="block text-[#8B95A5] text-sm mb-1">Client User ID *</label>
            <input value={clientUserId} onChange={e => setClientUserId(e.target.value)} placeholder="e.g. user_dev_client" className="qms-input w-full" data-testid="assign-client-user-id" />
            <p className="text-[10px] text-[#555] mt-1">The user_id of the client account to grant access</p>
          </div>
          <div>
            <label className="block text-[#8B95A5] text-sm mb-1">Client Organization</label>
            <input value={clientOrgId} onChange={e => setClientOrgId(e.target.value)} placeholder="Optional org identifier" className="qms-input w-full" data-testid="assign-client-org-id" />
          </div>
          {error && <p className="text-sm text-[#F87171]" data-testid="assign-error">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={handleAssign} disabled={!clientUserId.trim() || submitting} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="assign-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Assign
            </button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
